import { escapeHtml, initials, safeUrl } from '../ui.js';
import { threadFor, KIND_LABEL, KIND_TONE, KINDS } from '../golden-thread.js';

// THE GOLDEN THREAD PANEL — Track Golden Thread.html, artboards 2a and 3a–3e.
//
// A 640px slide-over holding ONE continuous history: oldest first, ending at
// today, every gap visible as a break in the line with the chase sitting in it.
//
// The panel it replaced was a directory — four flat lists of linked records,
// which is a thing you consult rather than a thing that tells you something.
// The line is the argument.
//
// The states are not decoration; each one is a rule (turn 3):
//   3a  a record opens IN PLACE. The thread is the point; navigating away to
//       read one node loses it.
//   3b  arrived from an Ask Track answer — the answer stays pinned at the top
//       and the thread opens at the record it cited.
//   3c  one day old. Two records and NOTHING MISSING: "of 2 so far", with what
//       normally comes next listed as expectation, not as failure.
//   3d  nothing missing. The state the feature argues for, so it has to feel
//       like an achievement rather than an empty screen.
//   3e  team access. HIDDEN MUST NOT LOOK BROKEN — a withheld record keeps the
//       solid line, takes a grey node, counts as present, and says only that
//       it is there.

const DATE = { day: '2-digit', month: 'short', year: '2-digit' };
const dayLabel = (t, now) => (now - t < 86400000 && new Date(t).getDate() === new Date(now).getDate()
  ? 'Today' : new Date(t).toLocaleDateString('en-GB', DATE));

const esc = (v) => escapeHtml(String(v == null ? '' : v));

/** The four-colour segmented bar: one segment per record, in thread order, so
 *  the shape of the relationship reads before any number does. */
function bar(nodes) {
  if (!nodes.length) return '';
  return `<div class="gt-bar">${nodes.map((n) => `<span style="background:${n.withheld ? 'var(--hair)' : KIND_TONE[n.kind]}"></span>`).join('')}</div>
    <div class="gt-legend">${KINDS.map((k) => `<span><i style="background:${KIND_TONE[k]}"></i>${esc(KIND_LABEL[k])}</span>`).join('')}</div>`;
}

/**
 * THE FOUR-NODE CHAIN (design_handoff_track_polish §9).
 *
 * Enquiry · Fee · Project · Invoice, in the same node states the project thread
 * uses: filled where the record exists, hollow where it does not, orange where
 * it is late. It answers the question the history underneath cannot answer at a
 * glance — did this piece of work get all the way through — and it answers it
 * in four shapes rather than forty rows.
 *
 * IT SITS ABOVE THE HISTORY RATHER THAN REPLACING IT. §9 also draws the body as
 * four sections grouped by record type; the 04/09 canvas Karan signed off
 * replaced exactly that with ONE history, deliberately, and this pass is not
 * the place to undo a later decision. The chain card is the part of §9 that
 * does not contradict it, so the chain card is the part that is built.
 */
/**
 * §9 draws the stages as Enquiry · Fee · Project · Invoice. Three of those are
 * records the thread holds; "Project" is not — in this data model a project is
 * a CONTAINER and the INSTRUCTION is the thing that exists, gets worked and
 * gets billed (CLAUDE.md, design laws). So the third stage is named for the
 * record that proves it rather than for the container it sits in. Same four
 * shapes, same states, and nothing on the screen claiming a record Track does
 * not keep.
 */
const CHAIN = [
  { key: 'enquiry', label: 'Enquiry' },
  { key: 'fee', label: 'Fee' },
  { key: 'instruction', label: 'Instructed' },
  { key: 'invoice', label: 'Invoiced' },
];

function chainCard(nodes, expected) {
  const hasNode = (kind) => nodes.some((n) => n.kind === kind && !n.withheld);
  // `expectations()` lists only what is MISSING, so a key appearing there is a
  // stage the thread expected and did not get — which is the break, and breaks
  // are orange wherever they are drawn.
  const awaited = new Set((expected || []).map((e) => e && e.key).filter(Boolean));
  const state = (c) => {
    const have = c.key === 'fee' ? !awaited.has('fee') : hasNode(c.key);
    if (have) return 'passed';
    return awaited.has(c.key) ? 'attention' : 'unreached';
  };
  return `<div class="gt-chain" role="img" aria-label="${esc(CHAIN.map((c) => `${c.label}: ${state(c) === 'passed' ? 'recorded' : 'missing'}`).join(', '))}">
    ${CHAIN.map((c, i) => `<span class="gt-chain-step">
        ${i ? `<span class="gt-chain-conn${state(CHAIN[i - 1]) === 'passed' ? ' on' : ''}" style="animation-delay:${320 + 80 * i}ms"></span>` : ''}
        <span class="gt-chain-node ${state(c)}" style="animation-delay:${260 + 80 * i}ms"></span>
        <span class="gt-chain-label">${esc(c.label)}</span>
      </span>`).join('')}
  </div>`;
}

/** The rail: a 1px line with a 9px dot on it. A break's dot is hollow and
 *  orange; a withheld record's dot is grey — but the LINE stays solid, because
 *  the record is there and somebody can read it. */
function rail(n, { broken = false } = {}) {
  const dot = broken
    ? '<span class="gt-dot gt-dot-break"></span>'
    : `<span class="gt-dot"${n.withheld ? ' data-withheld="1"' : ''} style="background:${n.withheld ? 'var(--hair-strong)' : KIND_TONE[n.kind]}"></span>`;
  return `<div class="gt-rail"><span class="gt-line"></span>${dot}</div>`;
}

/** One record. Clicking it expands in place (3a) — never navigates. */
function nodeRow(n, now, { cited = false } = {}) {
  const chips = (n.chips || []).map((c) => `<span class="gt-chip">${esc(c)}</span>`).join('');
  return `<div class="gt-row${cited ? ' is-cited' : ''}" data-node="${esc(n.id)}">
    <div class="gt-date">${esc(dayLabel(n.at, now))}</div>
    ${rail(n)}
    <div class="gt-node${n.withheld ? ' is-withheld' : ''}" role="button" tabindex="0">
      <div class="gt-kind" style="color:${n.withheld ? 'var(--ink-3)' : KIND_TONE[n.kind]}">${esc(n.eyebrow || KIND_LABEL[n.kind])}${cited ? '<span class="gt-cited">The record cited</span>' : ''}</div>
      <div class="gt-title">${esc(n.title)}</div>
      ${n.meta ? `<div class="gt-meta">${esc(n.meta)}</div>` : ''}
      ${chips ? `<div class="gt-chips">${chips}</div>` : ''}
      <div class="gt-open" hidden></div>
    </div>
  </div>`;
}

/**
 * A break, drawn IN the line where it happened. It names what is missing, who
 * it belongs to and how long it has sat — a gap nobody owns is a complaint.
 */
function breakRow(b, now, { cited = false } = {}) {
  const at = b.after;
  return `<div class="gt-row${cited ? ' is-cited' : ''}" data-break="${esc(b.key)}">
    <div class="gt-date">${esc(dayLabel(at, now))}</div>
    ${rail({ kind: 'note' }, { broken: true })}
    <div class="gt-breakwrap">
      <div class="gt-break">
        <div class="gt-break-head">
          <span class="gt-break-eyebrow">Thread breaks here${cited ? '<span class="gt-cited">The record cited</span>' : ''}</span>
          <span class="gt-spacer"></span>
          <span class="gt-age">${b.days} day${b.days === 1 ? '' : 's'} open</span>
        </div>
        <div class="gt-title">${esc(b.title)}</div>
        <div class="gt-why">${esc(b.why)}</div>
        <div class="gt-owner">
          ${b.ownerInitials ? `<span class="gt-av"${b.mine ? ' data-mine="1"' : ''}>${esc(b.ownerInitials)}</span>` : ''}
          <span class="gt-ownerline">${esc(b.ownerLine)}</span>
          <span class="gt-spacer"></span>
          <button class="gt-raise" data-raise="${esc(b.key)}">Raise task</button>
          <button class="gt-secondary" data-write="${esc(b.key)}">Write it now</button>
        </div>
        ${b.task ? `<div class="gt-task">
          <div class="gt-task-head"><span class="gt-task-ref">${esc(b.task.ref)}</span><span class="gt-task-brief">${esc(b.task.brief)}</span></div>
          <div class="gt-task-foot">
            <span class="gt-av gt-av-sm">${esc(initials(b.task.assigneeName))}</span>
            <span class="gt-task-meta">${esc(b.task.meta)}</span>
            <span class="gt-spacer"></span>
            <button class="gt-secondary" data-task="${esc(b.task.id)}">Open in Tasks</button>
          </div>
        </div>` : ''}
      </div>
    </div>
  </div>`;
}

/** What normally comes next (3c) — expectation, never failure. */
function expectedBlock(expected) {
  if (!expected.length) return '';
  return `<div class="gt-expect">
    <div class="gt-expect-h">What normally comes next</div>
    ${expected.map((e) => `<div class="gt-expect-row"><span>${esc(e.label)}</span><span class="gt-expect-hint">${esc(e.hint || '')}</span></div>`).join('')}
    <div class="gt-expect-note">If a fortnight passes with nothing added, Track will say so here rather than let it go quiet.</div>
  </div>`;
}

/**
 * The pinned Ask Track answer (3b), in the only three states it has.
 *
 * WAITING is a state and not a spinner: the thread is already readable
 * underneath, so the wait costs the reader nothing and is said in words.
 * NOTHING FOUND is a state too — Track holds no record that answers the
 * question, and it says so rather than dressing the thread up as an answer.
 * An answer that cannot be grounded in a record is never shown.
 */
function answerInner(a) {
  const line = a.answer ? esc(a.answer)
    : a.pending ? 'Reading the record\u2026'
    : a.gap ? `${esc(a.gap)} Here is everything Track does hold, in order.`
    : 'Every record Track holds on this, in order. Read it from the top.';
  return `<div class="gt-answer-head"><span class="gt-answer-brand">Ask Track<i>.</i></span><span class="gt-spacer"></span>${a.answer ? '<button class="gt-answer-back" data-back>Back to the answer</button>' : ''}</div>
    <div class="gt-answer-q">${esc(a.question)}</div>
    <div class="gt-answer-a${a.answer ? '' : ' is-quiet'}">${line}</div>${a.answer && a.email ? emailSource(a.email) : ''}`;
}

/** An answer that came from an email names it — it is not a node on the thread to open at. */
function emailSource(m) {
  const when = m.at ? new Date(m.at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '';
  const what = `${esc(m.subject || 'An email')}${m.from ? ` \u00b7 ${esc(m.from)}` : ''}${when ? ` \u00b7 ${esc(when)}` : ''}`;
  const href = m.permalink ? safeUrl(m.permalink) : '';
  return `<div class="gt-answer-src">From an email: ${href ? `<a href="${esc(href)}" target="_blank" rel="noopener">${what}</a>` : what}</div>`;
}

/**
 * The whole panel, as pure HTML. Kept pure so the conformance harness measures
 * the same markup the app renders (the harnesses that measured a stand-in went
 * green while the app was broken).
 *
 * @param vm.title    "8570 · Berrington Court"
 * @param vm.subtitle one line saying what this thread is
 * @param vm.scopes   [{ key, label, active }] — project / client / asset
 * @param vm.thread   threadFor(...)
 * @param vm.answer   { question, answer } when opened from Ask Track (3b)
 * @param vm.now      ms
 */
export function threadPanelHtml(vm = {}) {
  const { title = '', subtitle = '', scopes = [], answer = null, now = Date.now(), openAt = null } = vm;
  const t = vm.thread;
  const s = t.summary;

  // The line is one ordered sequence of records and breaks — a break is drawn
  // at the moment it happened, not collected in a list at the bottom, because
  // where it broke is the whole information.
  const items = [
    ...t.nodes.map((n) => ({ at: n.at, html: nodeRow(n, now, { cited: openAt && n.id === openAt }) })),
    ...t.breaks.map((b) => ({ at: b.after + 1, html: breakRow(b, now, { cited: openAt && b.key === openAt }) })),
  ].sort((a, b) => a.at - b.at);

  return `<div class="gt-scrim" data-scrim>
    <aside class="gt-panel" role="dialog" aria-label="Golden thread">
      ${answer ? `<div class="gt-answer">${answerInner(answer)}</div>` : ''}
      <div class="gt-body">
        <div class="gt-head">
          <div class="gt-head-t">
            <div class="gt-eyebrow">Golden thread</div>
            <div class="gt-h1">${esc(title)}</div>
            <div class="gt-sub">${esc(subtitle)}</div>
          </div>
          <button class="gt-close" data-close aria-label="Close">✕</button>
        </div>

        ${scopes.length ? `<div class="gt-scopes">${scopes.map((sc) => `<button class="gt-scope${sc.active ? ' is-on' : ''}" data-scope="${esc(sc.key)}"${sc.disabled ? ' disabled' : ''}>${esc(sc.label)}</button>`).join('')}</div>` : ''}

        <div class="gt-card">
          <div class="gt-card-top">
            <div class="gt-card-fig">
              <div class="gt-card-l">Records on the thread</div>
              <div class="gt-card-n"><span class="gt-done">${esc(s.doneLabel)}</span><span class="gt-of">${esc(s.totalLabel)}</span></div>
            </div>
            <span class="gt-spacer"></span>
            <div class="gt-card-r">
              <span class="gt-gapnote" style="color:${s.tone}">${esc(s.gapNote)}</span>
              ${s.raiseLabel ? `<button class="gt-raise" data-raise-all>${esc(s.raiseLabel)}</button>`
                : (s.sub ? `<span class="gt-gapsub">${esc(s.sub)}</span>` : '')}
            </div>
          </div>
          ${chainCard(t.nodes, t.expected)}
          ${bar(t.nodes)}
        </div>

        <div class="gt-thread">${items.map((i) => i.html).join('')}</div>
        ${expectedBlock(t.expected)}
        ${t.withheld && s.raiseLabel ? `<div class="gt-withheld-note">${t.withheld} record${t.withheld === 1 ? ' on this thread is' : 's on this thread are'} withheld at your access level. The thread is whole; you are not seeing all of it.</div>` : ''}
      </div>
    </aside>
  </div>`;
}

/** The expanded record (3a). Opened in place, under the node that was clicked. */
export function recordDetailHtml(n) {
  if (!n || n.withheld) return '<div class="gt-open-note">Nothing more to show at your access level.</div>';
  const r = n.record || {};
  const sect = (h, body) => (body ? `<div class="gt-sect"><div class="gt-sect-h">${esc(h)}</div>${body}</div>` : '');
  const people = (r.people || []).map((p) => `<div class="gt-person"><span class="gt-av">${esc(initials(p.name))}</span><span><b>${esc(p.name)}</b><i>${esc(p.role || '')}</i></span></div>`).join('');
  const evidence = (r.evidence || []).map((e) => `<span class="gt-chip">${esc(e.name || e)}</span>`).join('');
  const changed = (r.changed || []).map((c) => `<div class="gt-changed">${esc(c)}</div>`).join('');
  return `${sect('What was decided', r.detail ? `<div class="gt-detail">${esc(r.detail)}</div>` : '')}
    ${sect('Who was there', people)}
    ${sect('What proves it', evidence ? `<div class="gt-chips">${evidence}</div>` : '')}
    ${sect('What it changed', changed)}
    <div class="gt-open-actions"><button class="gt-secondary" data-full="${esc(n.id)}">Open the full record</button></div>`;
}

/**
 * Mount the panel. Returns { close }.
 *
 * @param opts.records  { enquiries, instructions, invoices, notes, canSee }
 * @param opts.on       { raise, openTask, openRecord, scope } — the caller owns
 *                      every side effect; this file only ever reads.
 */
export function openGoldenThread(opts = {}) {
  const { title, subtitle = '', scopes = [], records = {}, answer = null, session = {}, openAt = null, on = {} } = opts;
  const now = opts.now || Date.now();
  const thread = threadFor(records, {
    now, hasFee: !!opts.hasFee, fee: opts.fee || null,
    meId: session.uid || session.email || null, meName: session.displayName || null,
  });

  const host = document.createElement('div');
  host.innerHTML = threadPanelHtml({ title, subtitle, scopes, thread, answer, now, openAt });
  const root = host.firstElementChild;
  document.body.appendChild(root);
  document.body.style.overflow = 'hidden';

  const close = () => {
    root.remove();
    document.body.style.overflow = '';
    document.removeEventListener('keydown', onKey);
  };
  const onKey = (e) => { if (e.key === 'Escape') close(); };
  document.addEventListener('keydown', onKey);

  // Opened from an answer (3b): land on the record that answers it, rather than
  // at the top with the reader hunting for it.
  const cited = root.querySelector('.gt-row.is-cited');
  if (cited) cited.scrollIntoView({ block: 'center' });

  /**
   * Fill the answer in AFTER the panel is open.
   *
   * The thread opens the moment it is asked for and the answer arrives into it,
   * because the records are already the answer to most of these questions —
   * making the reader wait on a model before showing them their own history
   * would be the wrong way round. Marking the cited row is done here rather
   * than by re-rendering so that a record the reader has already opened stays
   * open under them.
   */
  const setAnswer = (next = {}) => {
    const block = root.querySelector('.gt-answer');
    if (!block) return;
    const merged = { ...(answer || {}), ...next };
    block.innerHTML = answerInner(merged);
    const id = merged.openAt;
    root.querySelectorAll('.gt-row.is-cited').forEach((r) => {
      r.classList.remove('is-cited');
      r.querySelectorAll('.gt-cited').forEach((tag) => tag.remove());
    });
    if (!id) return;
    const q = String(id).replace(/["\\]/g, '\\$&');
    const row = root.querySelector(`.gt-row[data-node="${q}"], .gt-row[data-break="${q}"]`);
    if (!row) return;
    row.classList.add('is-cited');
    const head = row.querySelector('.gt-kind') || row.querySelector('.gt-break-eyebrow');
    if (head) head.insertAdjacentHTML('beforeend', '<span class="gt-cited">The record cited</span>');
    row.scrollIntoView({ block: 'center', behavior: 'smooth' });
  };

  root.addEventListener('click', (e) => {
    if (e.target === root || e.target.closest('[data-close]')) return close();
    const back = e.target.closest('[data-back]');
    if (back) { close(); return on.backToAnswer && on.backToAnswer(); }

    const sc = e.target.closest('[data-scope]');
    if (sc) return on.scope && on.scope(sc.dataset.scope);

    const raise = e.target.closest('[data-raise]');
    if (raise) return on.raise && on.raise(thread.breaks.find((b) => b.key === raise.dataset.raise));
    if (e.target.closest('[data-raise-all]')) return on.raise && on.raise(thread.breaks);
    const write = e.target.closest('[data-write]');
    if (write) return on.write && on.write(thread.breaks.find((b) => b.key === write.dataset.write));

    const task = e.target.closest('[data-task]');
    if (task) { close(); return on.openTask && on.openTask(task.dataset.task); }
    const full = e.target.closest('[data-full]');
    if (full) return on.openRecord && on.openRecord(thread.nodes.find((n) => n.id === full.dataset.full));

    // A node opens IN PLACE (3a) — the thread is the point, so reading one
    // record must never cost you your place on it.
    const row = e.target.closest('.gt-node');
    if (row) {
      const open = row.querySelector('.gt-open');
      const n = thread.nodes.find((x) => x.id === row.parentElement.dataset.node);
      if (open.hidden) { open.innerHTML = recordDetailHtml(n); open.hidden = false; row.classList.add('is-open'); }
      else { open.hidden = true; open.innerHTML = ''; row.classList.remove('is-open'); }
    }
  });
  root.addEventListener('keydown', (e) => {
    if ((e.key === 'Enter' || e.key === ' ') && e.target.classList.contains('gt-node')) { e.preventDefault(); e.target.click(); }
  });

  return { close, thread, setAnswer };
}
